import type { StatusColorName, StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiCancel,
  mdiCheckBold,
  mdiCloseCircleOutline,
  mdiEmailOutline,
  mdiProgressClock,
} from "@mdi/js"

import { contactStepExecutionStatusTooltip } from "../../../_logic/contact-step-execution-status"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"
import {
  displayContactChannelLabel,
  displayContactStepResultStatusLabel,
} from "../../../../../../../../_shared/_logic/outreach-presentation"
import type { ContactOrderPlanHint } from "../../../_logic/communication-plan-table-contract"

export const contactStepResultLabels: Record<string, string> = {
  APPOINTMENT_APPROVED: "Termin zugesagt",
  APPOINTMENT_SCHEDULED: "Termin vereinbart",
  APPOINTMENT_DECLINED: "Termin abgelehnt",
  CONFIRMED: "Termin bestätigt",
  COMPLETED: "Kontakt erledigt",
  DECLINED: "Termin abgelehnt",
  NO_RESPONSE: "Keine Antwort",
  NOT_REACHED: "Nicht erreicht",
  INVALID_CONTACT: "Kontakt ungültig",
  SKIPPED: "Übersprungen",
  CANCELED: "Abgebrochen",
}

export const contactStepResultInfo: Record<
  string,
  {
    label: string
    description: string
    color: StatusColorName
    icon: StatusDefinition["icon"]
  }
> = {
  APPOINTMENT_APPROVED: {
    label: contactStepResultLabels.APPOINTMENT_APPROVED,
    description: "Termin wurde bestätigt.",
    color: "emerald",
    icon: mdiCheckBold,
  },
  APPOINTMENT_SCHEDULED: {
    label: contactStepResultLabels.APPOINTMENT_SCHEDULED,
    description: "Termin wurde vereinbart.",
    color: "emerald",
    icon: mdiCheckBold,
  },
  APPOINTMENT_DECLINED: {
    label: contactStepResultLabels.APPOINTMENT_DECLINED,
    description: "Termin wurde abgelehnt.",
    color: "rose",
    icon: mdiCancel,
  },
  CONFIRMED: {
    label: contactStepResultLabels.CONFIRMED,
    description: "Termin wurde bestätigt.",
    color: "emerald",
    icon: mdiCheckBold,
  },
  COMPLETED: {
    label: contactStepResultLabels.COMPLETED,
    description: "Kontakt wurde erfolgreich abgeschlossen.",
    color: "emerald",
    icon: mdiCheckBold,
  },
  DECLINED: {
    label: contactStepResultLabels.DECLINED,
    description: "Termin wurde abgelehnt.",
    color: "rose",
    icon: mdiCancel,
  },
  NO_RESPONSE: {
    label: contactStepResultLabels.NO_RESPONSE,
    description: "Keine Antwort erhalten.",
    color: "amber",
    icon: mdiProgressClock,
  },
  NOT_REACHED: {
    label: contactStepResultLabels.NOT_REACHED,
    description: "Kontakt nicht erreicht.",
    color: "amber",
    icon: mdiCloseCircleOutline,
  },
  INVALID_CONTACT: {
    label: contactStepResultLabels.INVALID_CONTACT,
    description: "Kontaktweg ist ungültig.",
    color: "orange",
    icon: mdiCloseCircleOutline,
  },
  SKIPPED: {
    label: contactStepResultLabels.SKIPPED,
    description: "Schritt wurde übersprungen.",
    color: "neutral",
    icon: mdiProgressClock,
  },
  CANCELED: {
    label: contactStepResultLabels.CANCELED,
    description: "Schritt wurde abgebrochen.",
    color: "neutral",
    icon: mdiCancel,
  },
}

export const contactOrderStepStatusTooltip = contactStepExecutionStatusTooltip

export const contactStepResultTooltip = Object.values(contactStepResultInfo)
  .map(({ label, description }) => `${label}: ${description}`)
  .join("\n")

const lowValueDescriptions = new Set([
  "Kontakt erfolgreich abgeschlossen.",
  "Termin wurde bestätigt.",
  "Keine Antwort erhalten.",
  "Kontakt nicht erreicht.",
  "Kontakt ungültig.",
  "Schritt wurde übersprungen.",
  "Schritt wurde abgebrochen.",
  "Termin wurde abgelehnt.",
  "Ergebnis dokumentiert.",
])

const reasonHints: Record<string, string> = {
  wrong_recipient: "Abgebrochen, weil derselbe Empfänger als falsch gemeldet wurde.",
  manual_cancel: "Schritt manuell abgebrochen.",
  wait_window_expired: "Keine Antwort im Wartefenster.",
  no_response: "Keine Antwort erhalten.",
  delivery_failed: "Nachricht konnte nicht zugestellt werden.",
  invalid_contact: "Kontakt ungültig.",
  canceled: "Schritt wurde abgebrochen.",
  CANCELED: "Ankündigungsplan abgebrochen.",
  appointment_canceled_by_bot: "Ankündigungsplan abgebrochen, weil der Termin abgesagt wurde.",
  appointment_canceled_by_portal: "Ankündigungsplan abgebrochen, weil der Termin abgesagt wurde.",
}

const genericNotes = new Set([
  "kontakt per e-mail",
  "kontakt per email",
  "kontakt per sms",
  "kontakt per telefon",
  "kontakt per brief",
  "telefonische erinnerung",
  "terminbrief",
])

const normalizeText = (value?: unknown) => {
  if (value === null || value === undefined) return null

  const text = typeof value === "string" ? value : String(value)
  const trimmed = text.trim()

  return trimmed || null
}

const normalizeStatus = (value?: unknown) => normalizeText(value)?.toUpperCase() ?? null

const normalizeStrategy = (value?: unknown) => normalizeText(value)?.toLowerCase() ?? null

const reasonFromMeta = (meta?: Record<string, unknown> | null) => {
  const reason = normalizeText(meta?.reason)

  if (!reason) return null

  return reason
}

const hintFromReason = (reason?: string | null) => {
  if (!reason) return null

  return reasonHints[reason] ?? reasonHints[reason.toLowerCase()] ?? null
}

const isMeaningfulDescription = (value?: string | null) => {
  const text = normalizeText(value)

  return Boolean(text && !lowValueDescriptions.has(text))
}

export const isMeaningfulContactStepNote = (value?: string | null) => {
  const text = normalizeText(value)

  if (!text) return false

  return !genericNotes.has(text.toLowerCase())
}

const shortText = (value?: string | null, maxLength = 42) => {
  const text = normalizeText(value)

  if (!text) return null

  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text
}

const isDigitalChannel = (channel?: string | null) => {
  const normalized = normalizeStatus(channel)

  return normalized === "EMAIL" || normalized === "MOBILE_MESSAGE" || normalized === "PHONE"
}

const getCandidateField = (candidate?: Record<string, unknown> | null, ...keys: string[]) => {
  if (!candidate) return null

  for (const key of keys) {
    const value = normalizeText(candidate[key])

    if (value) return value
  }

  return null
}

const getContactPointValue = (
  channel?: string | null,
  contactPoint?: ContactPoint | null,
  candidate?: Record<string, unknown> | null,
) =>
  getCandidateField(candidate, "value", "normalized_value", "label") ||
  normalizeText(contactPoint?.value) ||
  normalizeText(contactPoint?.normalized_value) ||
  null

const isVerifiedContactTarget = (
  contactPoint?: ContactPoint | null,
  candidate?: Record<string, unknown> | null,
) => {
  if (typeof candidate?.is_verified === "boolean") return candidate.is_verified

  return Boolean(contactPoint?.is_verified)
}

const planningHintTooltip = ({
  description,
}: {
  title: string
  description: string
  channel?: string | null
  contactPointValue?: string | null
  verification?: string | null
  strategy?: string | null
}) => buildRichTooltip([tooltipText(description)])

export const buildContactStepHintPresentation = ({
  channel,
  contactPoint,
  candidate,
  targetContactPointUsable = true,
  scheduleTimeStrategy,
  executionMode,
  hasVerifiedDigitalStep = false,
}: {
  channel?: string | null
  contactPoint?: ContactPoint | null
  candidate?: Record<string, unknown> | null
  targetContactPointUsable?: boolean
  scheduleTimeStrategy?: string | null
  executionMode?: string | null
  hasVerifiedDigitalStep?: boolean
}): ContactOrderPlanHint | null => {
  const normalizedChannel = normalizeStatus(channel)
  const normalizedExecutionMode = normalizeStrategy(executionMode)
  const normalizedScheduleStrategy = normalizeStrategy(scheduleTimeStrategy)
  const contactPointValue = getContactPointValue(channel, contactPoint, candidate)

  if (!targetContactPointUsable) {
    return {
      label: "Kontaktpunkt prüfen",
      subLabel: "nicht nutzbar",
      color: "orange",
      icon: mdiCloseCircleOutline,
      tooltipHtml: planningHintTooltip({
        title: "Kontaktpunkt prüfen",
        description:
          "Dieser Kontaktweg kann derzeit nicht verwendet werden. Bitte die Kontaktdaten prüfen.",
        channel,
        contactPointValue,
        verification: "nicht nutzbar",
      }),
    }
  }

  if (normalizedChannel === "LETTER" || normalizedExecutionMode === "manual_letter_delivery") {
    return {
      label: "Manuell zustellen",
      subLabel: "Terminbrief vorbereiten",
      color: "blue",
      icon: mdiEmailOutline,
      tooltipHtml: planningHintTooltip({
        title: "Manuelle Briefzustellung",
        description: "Brief drucken und zustellen. Danach als erledigt markieren.",
        channel,
        strategy: "Brief drucken oder zustellen",
      }),
    }
  }

  if (!isDigitalChannel(channel)) return null

  if (normalizedScheduleStrategy === "verified_channel_compressed") {
    return {
      label: "Zeit knapp",
      subLabel: "verifizierter Kanal priorisiert",
      color: "amber",
      icon: mdiProgressClock,
      tooltipHtml: planningHintTooltip({
        title: "Zeit knapp",
        description:
          "Die Kontaktaufnahme wird vorgezogen, damit der Kunde rechtzeitig informiert wird.",
        channel,
        contactPointValue,
        verification: isVerifiedContactTarget(contactPoint, candidate)
          ? "verifiziert"
          : "nicht verifiziert",
        strategy: "komprimierte Planung",
      }),
    }
  }

  if (isVerifiedContactTarget(contactPoint, candidate)) {
    return {
      label: "Verifiziert",
      subLabel: "vom Nutzer angegeben",
      color: "emerald",
      icon: mdiCheckBold,
      tooltipHtml: planningHintTooltip({
        title: "Verifizierter Kontaktweg",
        description: "Wird bei der automatischen Kontaktaufnahme bevorzugt.",
        channel,
        contactPointValue,
        verification: "verifiziert",
      }),
    }
  }

  if (hasVerifiedDigitalStep) {
    return {
      label: "Nicht verifiziert",
      subLabel: "zusätzlicher Versuch",
      color: "neutral",
      icon: mdiProgressClock,
      tooltipHtml: planningHintTooltip({
        title: "Nicht verifizierter Kontaktweg",
        description: "Zusätzlich zu einem bereits bestätigten Kontaktweg eingeplant.",
        channel,
        contactPointValue,
        verification: "nicht verifiziert",
      }),
    }
  }

  return {
    label: "Nicht verifiziert",
    subLabel: "keine Historie",
    color: "neutral",
    icon: mdiProgressClock,
    tooltipHtml: planningHintTooltip({
      title: "Nicht verifizierter Kontaktweg",
      description: "Über diesen Kontaktweg gab es bisher keine erfolgreiche Rückmeldung.",
      channel,
      contactPointValue,
      verification: "nicht verifiziert",
    }),
  }
}

const resultHintTooltip = ({
  title,
  description,
  detail,
  reason,
  recordedAt,
  lastContactTooltip,
}: {
  title: string
  description?: string | null
  detail?: string | null
  reason?: string | null
  recordedAt?: string | null
  lastContactTooltip?: string | null
}) =>
  buildRichTooltip([
    tooltipTitle(title),
    description ? tooltipText(description) : null,
    detail ? tooltipField("Detail", detail) : null,
    reason ? tooltipField("Grund", reason) : null,
    recordedAt ? tooltipField("Erfasst", recordedAt) : null,
    lastContactTooltip ? tooltipText(lastContactTooltip) : null,
  ])

export const buildContactStepPresentation = ({
  stepStatus,
  resultStatus,
  resultDescription,
  resultMeta,
  resultRecordedAt,
  lastContactLabel,
  lastContactTooltip,
  notes,
  channel,
  contactPoint,
  candidate,
  targetContactPointUsable,
  scheduleTimeStrategy,
  executionMode,
  hasVerifiedDigitalStep,
  formatDateTime,
}: {
  stepStatus?: string | null
  resultStatus?: string | null
  resultDescription?: string | null
  resultMeta?: Record<string, unknown> | null
  resultRecordedAt?: string | null
  lastContactLabel?: string | null
  lastContactTooltip?: string | null
  notes?: string | null
  channel?: string | null
  contactPoint?: ContactPoint | null
  candidate?: Record<string, unknown> | null
  targetContactPointUsable?: boolean
  scheduleTimeStrategy?: string | null
  executionMode?: string | null
  hasVerifiedDigitalStep?: boolean
  formatDateTime: (value?: string | null) => string
}) => {
  const normalizedStepStatus = normalizeStatus(stepStatus)
  const normalizedResultStatus =
    normalizeStatus(resultStatus) || (normalizedStepStatus === "CANCELED" ? "CANCELED" : null)
  const resultInfo = normalizedResultStatus
    ? (contactStepResultInfo[normalizedResultStatus] ?? {
        label: displayContactStepResultStatusLabel(normalizedResultStatus) ?? "Ergebnis unbekannt",
        description: "Unbekanntes Ergebnis.",
        color: "blue",
        icon: mdiProgressClock,
      })
    : null
  const resultDefinition: StatusDefinition<string> | null = resultInfo
    ? {
        value: normalizedResultStatus as string,
        label: resultInfo.label,
        description: resultInfo.description,
        color: resultInfo.color,
        icon: resultInfo.icon,
      }
    : null
  const recordedAt = resultRecordedAt ? formatDateTime(resultRecordedAt) : null
  const reason = reasonFromMeta(resultMeta)
  const descriptionHint = isMeaningfulDescription(resultDescription)
    ? normalizeText(resultDescription)
    : null
  const reasonHint = hintFromReason(reason)
  const noteHint = isMeaningfulContactStepNote(notes) ? normalizeText(notes) : null
  const resultText =
    resultDefinition?.label ||
    (lastContactLabel && lastContactLabel !== "Noch nicht ausgeführt"
      ? lastContactLabel
      : "Noch kein Ergebnis")
  const tooltipParts = [
    resultInfo?.description,
    descriptionHint,
    reasonHint && reasonHint !== descriptionHint ? reasonHint : null,
    recordedAt ? `Erfasst: ${recordedAt}` : null,
    lastContactTooltip,
  ].filter(Boolean)
  const resultOrNoteHint: ContactOrderPlanHint | null = resultDefinition
    ? {
        label: resultDefinition.label,
        subLabel: shortText(descriptionHint || reasonHint) ?? "Ergebnis dokumentiert",
        color: resultInfo?.color ?? "blue",
        icon: resultDefinition.icon,
        tooltipHtml: resultHintTooltip({
          title: resultDefinition.label,
          description: resultInfo?.description,
          detail: descriptionHint,
          reason: reasonHint && reasonHint !== descriptionHint ? reasonHint : null,
          recordedAt,
          lastContactTooltip,
        }),
      }
    : descriptionHint || reasonHint
      ? {
          label: "Hinweis",
          subLabel: shortText(descriptionHint || reasonHint),
          color: "neutral",
          icon: mdiProgressClock,
          tooltipHtml: resultHintTooltip({
            title: "Hinweis",
            detail: descriptionHint,
            reason: reasonHint && reasonHint !== descriptionHint ? reasonHint : null,
            recordedAt,
            lastContactTooltip,
          }),
        }
      : noteHint
        ? {
            label: "Notiz",
            subLabel: shortText(noteHint),
            color: "neutral",
            icon: mdiProgressClock,
            tooltipHtml: resultHintTooltip({
              title: "Notiz",
              detail: noteHint,
              recordedAt,
              lastContactTooltip,
            }),
          }
        : null
  const planningHint = buildContactStepHintPresentation({
    channel,
    contactPoint,
    candidate,
    targetContactPointUsable,
    scheduleTimeStrategy,
    executionMode,
    hasVerifiedDigitalStep,
  })
  const hint = resultOrNoteHint ?? planningHint
  const hintText = hint?.label ?? "–"

  return {
    resultDefinition,
    resultText,
    resultTooltip: tooltipParts.join("\n") || null,
    hint,
    hintText,
    hintTooltip: hint?.tooltipHtml ?? null,
  }
}
